/**
 * PermissionsContext — carrega permissões efetivas do usuário logado.
 *
 * Fonte: GET /api/rbac/me/effective
 *
 * Expõe:
 *   - permissions: Set<string> (keys "module.action")
 *   - role, roleLabel, companyScope, assignedCompanyIds
 *   - hasPermission(key: string) → boolean
 *   - hasAny(...keys) → boolean
 *   - canAccessCompany(companyId: string) → boolean
 *   - reload() → refetch
 */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { api, getToken } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";

const PermissionsContext = createContext(null);

export function PermissionsProvider({ children }) {
  const { user } = useAuth();
  const [state, setState] = useState({
    role: null,
    roleLabel: null,
    companyScope: null,
    permissions: new Set(),
    moduleAccess: {},
    assignedCompanyIds: null, // null = all_in_tenant / all_tenants
    loading: true,
  });

  const load = useCallback(async () => {
    if (!getToken() || !user) {
      setState((s) => ({
        ...s,
        loading: false,
        permissions: new Set(),
      }));
      return;
    }

    const role = String(user?.role || "").trim().toLowerCase();

    if (
      role === "commercial_master" ||
      role === "commercial_consultant"
    ) {
      setState({
        role,
        roleLabel:
          role === "commercial_master"
            ? "Administrador Comercial"
            : "Consultor Comercial",
        companyScope: null,
        permissions: new Set(),
        moduleAccess: {},
        assignedCompanyIds: [],
        loading: false,
      });
      return;
    }

    try {
      const { data } = await api.get("/rbac/me/effective");
      setState({
        role: data.role,
        roleLabel: data.role_label,
        companyScope: data.company_scope,
        permissions: new Set(data.permissions || []),
        moduleAccess: data.module_access || {},
        assignedCompanyIds: data.assigned_company_ids,
        loading: false,
      });
    } catch {
      setState((s) => ({ ...s, loading: false, permissions: new Set() }));
    }
  }, [user]);

  useEffect(() => {
    load();
    // Recarrega quando token muda (login/logout — mesmo tab OR outra tab)
    const onAuthChanged = () => load();
    const onStorage = (e) => {
      if (e.key === "simpler_access" || e.key === "simpler_refresh") onAuthChanged();
    };
    window.addEventListener("simpler:auth-changed", onAuthChanged);
    window.addEventListener("storage", onStorage);
    return () => {
      window.removeEventListener("simpler:auth-changed", onAuthChanged);
      window.removeEventListener("storage", onStorage);
    };
  }, [load]);

  const hasPermission = useCallback(
    (key) => state.permissions.has(key),
    [state.permissions],
  );

  const hasAny = useCallback(
    (...keys) => keys.some((k) => state.permissions.has(k)),
    [state.permissions],
  );

  const canAccessCompany = useCallback(
    (companyId) => {
      if (!companyId) return false;
      if (state.assignedCompanyIds == null) return true; // all
      return state.assignedCompanyIds.includes(companyId);
    },
    [state.assignedCompanyIds],
  );

  const isModuleEnabled = useCallback(
    (moduleSlug) => {
      // If moduleAccess explicitly disables it, block.
      const explicit = state.moduleAccess[moduleSlug];
      if (explicit === false) return false;
      // Else: user has at least .view for this module?
      return state.permissions.has(`${moduleSlug}.view`);
    },
    [state.moduleAccess, state.permissions],
  );

  const value = useMemo(
    () => ({
      ...state,
      hasPermission,
      hasAny,
      canAccessCompany,
      isModuleEnabled,
      reload: load,
    }),
    [state, hasPermission, hasAny, canAccessCompany, isModuleEnabled, load],
  );

  return (
    <PermissionsContext.Provider value={value}>
      {children}
    </PermissionsContext.Provider>
  );
}

export function usePermissions() {
  const ctx = useContext(PermissionsContext);
  if (!ctx)
    throw new Error("usePermissions must be used within PermissionsProvider");
  return ctx;
}

/** Hook prático: boolean por permission key. */
export function useHasPermission(key) {
  const { hasPermission } = usePermissions();
  return hasPermission(key);
}

/** Hook prático: verifica acesso a uma empresa específica. */
export function useCanAccessCompany(companyId) {
  const { canAccessCompany } = usePermissions();
  return canAccessCompany(companyId);
}
